import { Link, useParams } from 'react-router-dom';
import { Home, Search, HelpCircle, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SharedHeader from './SharedHeader';
import Footer from './Footer';

const NotFound = () => {
  const { language } = useLanguage();
  const { lang } = useParams<{ lang: string }>();

  const content = {
    en: {
      title: '404 - Page Not Found',
      subtitle: 'Oops! The page you\'re looking for doesn\'t exist.',
      description: 'The page might have been moved, deleted, or you entered the wrong URL. Don\'t worry, we\'ll help you find what you\'re looking for.',
      suggestions: {
        title: 'Here are some helpful links:',
        items: [
          {
            icon: <Home className="w-5 h-5" />,
            text: 'Go to Homepage',
            description: 'Start creating QR codes',
            path: `/${lang || 'en'}`
          },
          {
            icon: <HelpCircle className="w-5 h-5" />,
            text: 'Help & Documentation',
            description: 'Learn how to use QRCodeForge',
            path: `/${lang || 'en'}/help`
          },
          {
            icon: <Search className="w-5 h-5" />,
            text: 'About Us',
            description: 'Learn more about QRCodeForge',
            path: `/${lang || 'en'}/about`
          }
        ]
      },
      backButton: 'Go Back',
      reportIssue: 'If you believe this is an error, please contact us at support@qrcodeforge.com'
    },
    zh: {
      title: '404 - 页面未找到',
      subtitle: '抱歉！您要查找的页面不存在。',
      description: '页面可能已被移动、删除，或者您输入了错误的网址。别担心，我们会帮您找到您要查找的内容。',
      suggestions: {
        title: '以下是一些有用的链接：',
        items: [
          {
            icon: <Home className="w-5 h-5" />,
            text: '返回首页',
            description: '开始创建二维码',
            path: `/${lang || 'zh'}`
          },
          {
            icon: <HelpCircle className="w-5 h-5" />,
            text: '帮助文档',
            description: '了解如何使用 QRCodeForge',
            path: `/${lang || 'zh'}/help`
          },
          {
            icon: <Search className="w-5 h-5" />,
            text: '关于我们',
            description: '了解更多关于 QRCodeForge',
            path: `/${lang || 'zh'}/about`
          }
        ]
      },
      backButton: '返回',
      reportIssue: '如果您认为这是一个错误，请通过 support@qrcodeforge.com 联系我们'
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Main Content */}
        <div className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">

            {/* 404 Visual */}
            <div className="mb-8">
              <div className="text-8xl md:text-9xl font-bold text-white/10 mb-4">
                404
              </div>
            </div>

            {/* Error Message */}
            <div className="mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {currentContent.title}
              </h1>
              <p className="text-xl text-gray-300 mb-6">
                {currentContent.subtitle}
              </p>
              <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
                {currentContent.description}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => window.history.back()}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors backdrop-blur-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {currentContent.backButton}
              </button>
              <Link
                to={`/${lang || 'en'}`}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                {currentContent.suggestions.items[0].text}
              </Link>
            </div>

            {/* Helpful Links */}
            <div className="mb-12">
              <h2 className="text-xl font-semibold text-white mb-6">
                {currentContent.suggestions.title}
              </h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                {currentContent.suggestions.items.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors group"
                  >
                    <div className="flex justify-center mb-3 text-blue-300 group-hover:text-blue-200 transition-colors">
                      {item.icon}
                    </div>
                    <h3 className="font-medium text-white mb-2">{item.text}</h3>
                    <p className="text-sm text-gray-300">{item.description}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="text-center">
              <p className="text-sm text-gray-400">
                {currentContent.reportIssue}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default NotFound;